import React from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  Sun, Moon, RotateCcw, Phone, Clock, Plane, Calendar
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ShiftPreference } from '@/types/jobWizard';
import { motion } from 'framer-motion';

interface JobShiftPreferencesBadgesProps {
  preferences: ShiftPreference[];
  className?: string;
}

const SHIFT_CONFIG: Record<ShiftPreference, { 
  label: string; 
  icon: React.ElementType; 
  color: string;
}> = {
  day_shift: { 
    label: 'Day Shift', 
    icon: Sun, 
    color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700/40' 
  },
  night_shift: { 
    label: 'Night Shift', 
    icon: Moon, 
    color: 'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-700/40' 
  },
  rotating: { 
    label: 'Rotating Schedule', 
    icon: RotateCcw, 
    color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700/40' 
  },
  on_call: { 
    label: 'On-Call', 
    icon: Phone, 
    color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700/40' 
  },
  flexible: { 
    label: 'Flexible Hours', 
    icon: Clock, 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700/40' 
  },
  travel_required: { 
    label: 'Travel Required', 
    icon: Plane, 
    color: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700/40' 
  },
  weekends: { 
    label: 'Weekends', 
    icon: Calendar, 
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-700/40' 
  },
};

const JobShiftPreferencesBadges: React.FC<JobShiftPreferencesBadgesProps> = ({
  preferences,
  className,
}) => {
  if (!preferences || preferences.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {preferences.map((pref, index) => {
        const config = SHIFT_CONFIG[pref];
        if (!config) return null;
        
        const Icon = config.icon;
        
        return (
          <motion.div
            key={pref}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
          >
            <Badge className={cn("gap-1.5 border", config.color)}>
              <Icon className="w-3 h-3" />
              {config.label}
            </Badge>
          </motion.div>
        );
      })}
    </div>
  );
};

export default JobShiftPreferencesBadges;
